import "../styles/add.css";

import { useState, useContext, useEffect, useRef } from "react";
import { AuthContext } from "../services/contexts/AuthContext";
import { SizePicker } from "./SizePicker";
import { defaultSizeKeys } from "../utils/productSizes";
import { useDuplicateName, findDuplicateName } from "../hooks/useDuplicateName";
import { DuplicateNameNotice } from "./DuplicateNameNotice";
import firebase from "./firebase";

const CLOUDINARY_CLOUD = 'dibfyycgl';
const CLOUDINARY_PRESET = 'handdrawn_clothes';

export const AddProduct = ({ onAddProductSubmit }) => {
  const { currentUser } = useContext(AuthContext);

  const [values, setValue] = useState({
    name: "",
    description: "",
    price: "",
    img: "",
    images: [""],
    video: "",
    owner: currentUser ? currentUser.uid : null,
    type: "мъже",
    catalogNumber: "",
    sizes: defaultSizeKeys({ type: "мъже" }),
  });

  const [productTypes, setProductTypes] = useState([]);
  const [showAddType, setShowAddType] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRefs = useRef([]);
  const { match: duplicateName } = useDuplicateName(values.name);

  useEffect(() => {
    const loadProductTypes = async () => {
      const db = firebase.firestore();
      try {
        const doc = await db.collection('Settings').doc('productTypes').get();
        if (doc.exists) {
          setProductTypes(doc.data().types || []);
        } else {
          const defaultTypes = [
            "мъже", "жени", "деца", "якета", "обувки", "пътечки",
            "коледа", "картички", "щампи", "чаши", "декупаж",
            "керамика", "семейство", "месечинки"
          ];
          await db.collection('Settings').doc('productTypes').set({ types: defaultTypes });
          setProductTypes(defaultTypes);
        }
      } catch (error) {
        console.error("Error loading product types:", error);
      }
    };
    loadProductTypes();
  }, []);

  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    setValue((state) => ({
      ...state,
      [name]: value,
      // Picking a category pre-selects the sizes that category usually needs;
      // the checkboxes below stay editable for the exceptions.
      ...(name === 'type' && { sizes: defaultSizeKeys({ type: value }) }),
    }));
  };

  const handleAddNewType = async () => {
    if (!newTypeName.trim()) {
      alert("Моля, въведете име на категория");
      return;
    }
    const db = firebase.firestore();
    try {
      const updatedTypes = [...productTypes, newTypeName.trim()];
      await db.collection('Settings').doc('productTypes').set({ types: updatedTypes });
      setProductTypes(updatedTypes);
      setValue((state) => ({ ...state, type: newTypeName.trim() }));
      setNewTypeName("");
      setShowAddType(false);
      alert("Категорията е добавена успешно!");
    } catch (error) {
      console.error("Error adding new type:", error);
      alert("Грешка при добавяне на категория");
    }
  };

  const handleImageChange = (index, value) => {
    const newImages = [...values.images];
    newImages[index] = value;
    setValue((state) => ({ ...state, images: newImages }));
  };

  const handleFileUpload = async (index, file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert("Моля, качете само изображения (JPG, PNG, WEBP, GIF)");
      return;
    }

    setUploadProgress(prev => ({ ...prev, [index]: 0 }));

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_PRESET);

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(prev => ({ ...prev, [index]: pct }));
        }
      };

      xhr.onload = () => {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status === 200) {
          handleImageChange(index, data.secure_url);
        } else {
          alert('Грешка при качване на снимката');
        }
        setUploadProgress(prev => { const n = { ...prev }; delete n[index]; return n; });
      };

      xhr.onerror = () => {
        alert('Грешка при качване на снимката');
        setUploadProgress(prev => { const n = { ...prev }; delete n[index]; return n; });
      };

      xhr.send(formData);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadProgress(prev => { const n = { ...prev }; delete n[index]; return n; });
    }
  };

  const addImageField = () => {
    setValue((state) => ({ ...state, images: [...state.images, ""] }));
  };

  const removeImageField = (index) => {
    if (values.images.length > 1) {
      const newImages = values.images.filter((_, i) => i !== index);
      setValue((state) => ({ ...state, images: newImages }));
      setUploadProgress(prev => { const n = { ...prev }; delete n[index]; return n; });
    }
  };

  const generateCatalogNumber = () => {
    const now = new Date();
    const dateStr = now.getFullYear().toString() +
      (now.getMonth() + 1).toString().padStart(2, '0') +
      now.getDate().toString().padStart(2, '0');
    const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
    setValue((state) => ({ ...state, catalogNumber: `CAT-${dateStr}-${randomStr}` }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (Object.keys(uploadProgress).length > 0) {
      alert("Моля, изчакайте приключването на качването на снимките");
      return;
    }

    const existing = await findDuplicateName(values.name);
    if (existing) {
      alert(
        `Вече съществува продукт с името „${existing.name}".\n\n` +
        `Google индексира само едната от две страници с еднакво заглавие. ` +
        `Добави нещо отличително към името, преди да запазиш.`
      );
      return;
    }

    const filteredImages = values.images.filter(img => img.trim() !== "");

    let catalogNumber = values.catalogNumber.trim();
    if (!catalogNumber) {
      const now = new Date();
      const dateStr = now.getFullYear().toString() +
        (now.getMonth() + 1).toString().padStart(2, '0') +
        now.getDate().toString().padStart(2, '0');
      const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
      catalogNumber = `CAT-${dateStr}-${randomStr}`;
    }

    const submissionValues = {
      ...values,
      img: filteredImages[0] || "",
      images: filteredImages,
      catalogNumber,
    };
    onAddProductSubmit(submissionValues);
  };

  const uploadBtnStyle = {
    padding: "8px 14px",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontSize: "13px",
  };

  return (
    <section id="create-page" className="auth">
      <form id="create" onSubmit={onSubmit}>
        <div className="container">
          <h1>Add Product</h1>
          <label htmlFor="name">Name:</label>
          <input
            value={values.name}
            onChange={onChangeHandler}
            type="text"
            id="title"
            name="name"
            placeholder="Enter name..."
            style={duplicateName ? { border: "2px solid #e0a800" } : undefined}
          />
          <DuplicateNameNotice match={duplicateName} />
          <label htmlFor="details">Description:</label>
          <input
            value={values.description}
            onChange={onChangeHandler}
            type="text"
            id="details"
            name="description"
            placeholder="Enter product details..."
          />
          <label htmlFor="price">Цена (€):</label>
          <input
            value={values.price}
            onChange={onChangeHandler}
            type="number"
            id="price"
            name="price"
            min={0.01}
            step={0.01}
            placeholder={0}
            style={{ width: "230px" }}
          />

          <label htmlFor="type">Type:</label>
          <select value={values.type} onChange={onChangeHandler} id="type" name="type">
            {productTypes.map((type) => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>

          <label style={{ marginTop: "12px" }}>За кои размери да пита продуктът:</label>
          <SizePicker
            value={values.sizes}
            onChange={(sizes) => setValue((state) => ({ ...state, sizes }))}
          />

          <button
            type="button"
            onClick={() => setShowAddType(!showAddType)}
            style={{ padding: "8px 20px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", marginTop: "10px", marginBottom: "15px" }}
          >
            {showAddType ? "Отказ" : "+ Добави нова категория"}
          </button>

          {showAddType && (
            <div style={{ marginBottom: "15px", padding: "15px", border: "2px solid #007bff", borderRadius: "4px", backgroundColor: "#f8f9fa" }}>
              <label htmlFor="newType">Име на нова категория:</label>
              <input
                type="text"
                id="newType"
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="Въведете име на категория..."
                style={{ marginBottom: "10px" }}
              />
              <button
                type="button"
                onClick={handleAddNewType}
                style={{ padding: "8px 20px", backgroundColor: "#28a745", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
              >
                Запази категория
              </button>
            </div>
          )}

          <label htmlFor="catalogNumber">Каталожен номер:</label>
          <div style={{ display: "flex", gap: "10px", marginBottom: "15px", alignItems: "center" }}>
            <input
              value={values.catalogNumber}
              onChange={onChangeHandler}
              type="text"
              id="catalogNumber"
              name="catalogNumber"
              placeholder="Оставете празно за автоматично генериране"
              style={{ flex: 1 }}
            />
            <button
              type="button"
              onClick={generateCatalogNumber}
              style={{ padding: "8px 20px", backgroundColor: "#6c757d", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", whiteSpace: "nowrap" }}
            >
              🔄 Генерирай
            </button>
          </div>

          <label>Снимки:</label>
          {values.images.map((image, index) => (
            <div key={index} style={{ marginBottom: "14px" }}>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                {/* Hidden real file input */}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  ref={el => fileInputRefs.current[index] = el}
                  onChange={(e) => handleFileUpload(index, e.target.files[0])}
                />
                {/* Upload button */}
                <button
                  type="button"
                  style={uploadBtnStyle}
                  onClick={() => fileInputRefs.current[index]?.click()}
                  disabled={uploadProgress[index] !== undefined}
                >
                  📁 Качи
                </button>
                {/* URL input as fallback */}
                <input
                  value={image}
                  onChange={(e) => handleImageChange(index, e.target.value)}
                  type="text"
                  placeholder={`Снимка ${index + 1} — URL или качи файл`}
                  style={{ flex: 1 }}
                />
                {values.images.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeImageField(index)}
                    style={{ padding: "5px 12px", backgroundColor: "#dc3545", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
                  >
                    ✕
                  </button>
                )}
              </div>
              {/* Upload progress bar */}
              {uploadProgress[index] !== undefined && (
                <div style={{ marginTop: "6px" }}>
                  <div style={{ height: "6px", backgroundColor: "#e9ecef", borderRadius: "3px", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${uploadProgress[index]}%`, backgroundColor: "#4CAF50", transition: "width 0.2s" }} />
                  </div>
                  <small style={{ color: "#666" }}>Качване... {uploadProgress[index]}%</small>
                </div>
              )}
              {/* Preview thumbnail */}
              {image && uploadProgress[index] === undefined && (
                <img
                  src={image}
                  alt={`preview ${index + 1}`}
                  style={{ marginTop: "6px", height: "60px", width: "60px", objectFit: "cover", borderRadius: "4px", border: "1px solid #ddd" }}
                />
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={addImageField}
            style={{ padding: "8px 20px", backgroundColor: "#28a745", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", marginBottom: "15px" }}
          >
            + Добави още снимка
          </button>

          {/* Video upload */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ fontWeight: 600, display: "block", marginBottom: "6px" }}>Видео на продукта (по избор):</label>
            <input
              type="file"
              accept="video/*"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                setUploadProgress(prev => ({ ...prev, video: 0 }));
                const formData = new FormData();
                formData.append('file', file);
                formData.append('upload_preset', CLOUDINARY_PRESET);
                const xhr = new XMLHttpRequest();
                xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/video/upload`);
                xhr.upload.onprogress = (ev) => {
                  if (ev.lengthComputable) {
                    setUploadProgress(prev => ({ ...prev, video: Math.round((ev.loaded / ev.total) * 100) }));
                  }
                };
                xhr.onload = () => {
                  const data = JSON.parse(xhr.responseText);
                  if (xhr.status === 200) {
                    setValue(prev => ({ ...prev, video: data.secure_url }));
                    setUploadProgress(prev => { const s = {...prev}; delete s.video; return s; });
                  }
                };
                xhr.send(formData);
              }}
              style={{ display: "block", marginBottom: "6px" }}
            />
            {uploadProgress.video !== undefined && (
              <div style={{ height: "6px", background: "#eee", borderRadius: "3px", marginBottom: "6px" }}>
                <div style={{ height: "100%", width: `${uploadProgress.video}%`, backgroundColor: "#4CAF50", transition: "width 0.2s", borderRadius: "3px" }} />
              </div>
            )}
            {values.video && <small style={{ color: "#4CAF50" }}>✓ Видеото е качено</small>}
          </div>

          <input className="btn submit" type="submit" value="Add" />
        </div>
      </form>
    </section>
  );
};
